import styled from 'styled-components'

import { Flex } from '../../layout'
import { H1, H2, H3, H4 } from '../../typography/Heading'

import type { FC, ReactNode } from 'react'

type AppHeaderTitleProps = {
  title?: { title: string; as: 'h1' | 'h2' | 'h3' | 'h4' }
  titleAfter?: ReactNode
  centered?: boolean
}

export const AppHeaderTitle: FC<AppHeaderTitleProps> = ({ title, titleAfter, centered }) => {
  const renderTitle = () => {
    if (!title) {
      return null
    }

    switch (title.as || 'h2') {
      case 'h1': {
        return <StHeading1 $centered={centered}>{title.title}</StHeading1>
      }
      case 'h2': {
        return <StHeading2 $centered={centered}>{title.title}</StHeading2>
      }
      case 'h3': {
        return <StHeading3 $centered={centered}>{title.title}</StHeading3>
      }
      case 'h4': {
        return <StHeading4 $centered={centered}>{title.title}</StHeading4>
      }
      default: {
        return <StHeading2 $centered={centered}>{title.title}</StHeading2>
      }
    }
  }

  return (
    <StContainer $centered={centered}>
      <StTitleWrapper>{renderTitle()}</StTitleWrapper>
      {titleAfter}
    </StContainer>
  )
}

const StContainer = styled(Flex)<{ $centered?: boolean }>`
  flex: ${({ $centered }) => ($centered === false ? 1 : '0 1 auto')};
  align-items: center;
  justify-content: ${({ $centered }) => ($centered === false ? 'flex-start' : 'center')};
  gap: ${({ theme }) => theme.UI.Spacing.Space2}px;
  min-width: 0; /* Allow flex item to shrink below content size */
  overflow: hidden; /* prevent children overflow */
`

const StTitleWrapper = styled.div`
  flex: 1;
  min-width: 0; /* critical for text-overflow in flex children */
`

const sharedHeadingStyles = `
  margin: 0;
  width: 100%;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* clamp to two lines */
  -webkit-box-orient: vertical;
`

const StHeading1 = styled(H1)<{ $centered?: boolean }>`
  ${sharedHeadingStyles}
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: ${({ $centered }) => ($centered === false ? 'left' : 'center')};
`

const StHeading2 = styled(H2)<{ $centered?: boolean }>`
  ${sharedHeadingStyles}
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: ${({ $centered }) => ($centered === false ? 'left' : 'center')};
`

const StHeading3 = styled(H3)<{ $centered?: boolean }>`
  ${sharedHeadingStyles}
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: ${({ $centered }) => ($centered === false ? 'left' : 'center')};
`

const StHeading4 = styled(H4)<{ $centered?: boolean }>`
  ${sharedHeadingStyles}
  color: ${({ theme }) => theme.Colors.Neutrals.Darkest};
  text-align: ${({ $centered }) => ($centered === false ? 'left' : 'center')};
`
